import { CommonModule } from '@angular/common';
import notify from 'devextreme/ui/notify';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DxCheckBoxModule,
  DxDataGridModule,
  DxFormModule,
  DxPopupModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxSortableModule,
  DxTagBoxModule,
  DxValidatorModule,
} from 'devextreme-angular';
import { DxiColumnModule, DxoLookupModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { DragChangeEvent, DragStartEvent, ReorderEvent } from 'devextreme/ui/sortable';
import { from, Subscription } from 'rxjs';
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SharedPipesModule } from 'src/app/pipes/shared.pipes.module';
import { TemplatesService } from 'src/app/services/templates.service';
import { DataAccessService } from 'src/app/services/data-access.service';
import { ConfigService } from 'src/app/services/config.service';
import { PhasesService } from 'src/app/services/phases.service';
import { TemplateApplyBannerComponent } from './components/template-apply-banner/template-apply-banner.component';
import { ConfigTaskNodeComponent } from './components/config-task-node/config-task-node.component';
import { ConfigTask, MappedConfigTask, MappedConfigTreeNode, TaskType, TaskTypeEnum } from './datatypes-taskconfig';
import { Milestone, Phase, TaskPackage } from 'src/app/interfaces/entities/project-configuration';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { GenericPopupComponent } from "src/app/components/generic-popup/generic-popup.component";
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { ProjectStatus } from '../../../../../interfaces/entities/project';

import { SearchableDropdownDirective } from '../../../../../directives/searchable-dropdown.directive';
@Component({
    selector: 'app-project-task-config',
    imports: [
        ConfigTaskNodeComponent,
        CommonModule,
        DxScrollViewModule,
        DxSortableModule,
        SharedPipesModule,
        GpdButtonComponent,
        DxDataGridModule,
        DxPopupModule,
        DxTagBoxModule,
        DxCheckBoxModule,
        DxiColumnModule,
        DxoLookupModule,
        DxoSelectionModule,
        DxSelectBoxModule,
        DxFormModule,
        GenericPopupComponent,
        DxValidatorModule,
        ValidationToastComponent,
        SvgIconComponent,
        SearchableDropdownDirective,
        TemplateApplyBannerComponent
    ],
    templateUrl: './project-task-config.component.html',
    styleUrl: './project-task-config.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectTaskConfigComponent implements OnInit, OnDestroy {
  // Page State
  TaskPackageNr: string | null = null;
  isLoading = false;

  // Task State - working directly with tree structure
  public allTasks: ConfigTask[] = [];
  public projectConfiguration: Phase[] | null = null;
  selectedTasks: number[] = [];

  public tasksTree: MappedConfigTask[] = [];

  public editingData: MappedConfigTask | null = null;

  public configurationTree: MappedConfigTreeNode[] = [];

  public routeParamsSubscription: Subscription;
  public projectConfigurationSubscription: Subscription;
  public areaTypesSubscription: Subscription;

  // Project selection state
  public showConfirmationPopup: boolean = false;
  public showProjects: boolean = false;
  confirmText = $localize`:@@confirm:Confirm`;
  saveText = $localize`:@@save:Save`;
  selectProjectsText = $localize`:@@selectProjects:Select Projects`;

  // Archive / reactivate state
  archivePopupVisible = false;
  reactivatePopupVisible = false;
  archiveImpactTasks: { taskId: number; taskTitle: string }[] = [];
  archiveOpenTaskCount = 0;
  archiveSetNotRequired = false;
  archiveInProgress = false;
  taskPendingArchive: MappedConfigTask | null = null;
  taskPendingReactivate: MappedConfigTask | null = null;

  // Delete confirmation state
  deletePopupVisible = false;
  taskPendingDelete: MappedConfigTask | null = null;
  deleteGroupWarningMessage = $localize`:@@deleteGroupWarning:Warning: All tasks within this group will also be deleted.`;
  deleteTaskWarningTitle = $localize`:@@deleteTaskWarningTitle:Warning`;
  deleteTaskWarningHtml =
    $localize`:@@deleteTaskWarningPara1:If you proceed, the task will be removed from all existing and future projects.` +
    '<br><br>' +
    $localize`:@@deleteTaskWarningPara2:All data associated with this task will be lost. Completed document tasks saved in SharePoint will remain in SharePoint.` +
    '<br><br>' +
    $localize`:@@deleteTaskWarningPara3:If you want to keep the task in existing projects but no longer use it for future projects, archive the task.`;
  cancelLabel = $localize`:@@cancel:Cancel`;
  deleteLabel = $localize`:@@delete:Delete`;
  archiveLabel = $localize`:@@archive:Archive`;
  private deleteTaskTitle = $localize`:@@deleteTask:Delete task`;
  private deleteGroupTitle = $localize`:@@deleteGroupTitle:Delete group`;

  get archiveNotRequiredLabel(): string {
    return $localize`:@@archiveSetNotRequired:Also set this task to "not required" in ${this.archiveOpenTaskCount}:count: existing projects.`;
  }

  get archiveConfirmLabel(): string {
    return this.archiveSetNotRequired
      ? $localize`:@@archiveConfirmWithUpdate:Archive & update ${this.archiveOpenTaskCount}:count: tasks`
      : this.archiveLabel;
  }

  get deletePopupTitle(): string {
    return this.taskPendingDelete?.type === 'group' ? this.deleteGroupTitle : this.deleteTaskTitle;
  }
  public projectList: any[] = [];
  public completedProjectCount: number = 0;
  public areaTypeList: any[] = [];
  public milestonesList: any[] = [];
  public selectedPhases: number[] = [];
  public selectedAreas: number[] = [];
  public selectedMilestones: number[] = [];
  public selectedProjects: number[] = [];
  public deselectedProjects: number[] = [];
  public filteredProjects: number[] = [];
  public manualProjects: number[] = [];
  public isAllSelected: boolean = false;
  public isFilterActive: boolean = false;
  public savedEvent: { task: MappedConfigTask; callback: (success: boolean) => void };

  // Site selection state
  public showSiteConfirmationPopup: boolean = false;
  public showSites: boolean = false;
  public siteList: any[] = [];
  public selectedSites: number[] = [];
  public deselectedSites: number[] = [];
  public manualSites: number[] = [];
  public isAllSitesSelected: boolean = false;
  @ViewChild('siteGrid') siteGrid: any;

  showMoveTasks: {
    tasks : number[];
    phaseId: number;
    milestoneId: number;
    taskPackageId: number;
  } | null = null;



  @ViewChild('grid') grid: any;
  @ViewChild('scrollView') scrollView: any;
  @ViewChild(TemplateApplyBannerComponent) banner?: TemplateApplyBannerComponent;
  constructor(
    private route: ActivatedRoute,
    private templateService: TemplatesService,
    private cdr: ChangeDetectorRef,
    private dataAccessService: DataAccessService,
    private configService: ConfigService,
    private router: Router
  ) {
    this.routeParamsSubscription = this.route.queryParams.subscribe(params => {
      if (
        this.selectedTaskPackageValue !== params.packageId ||
        this.selectedMilestoneValue !== params.milestoneId ||
        this.selectedPhaseValue !== params.phaseId
      ) {
        this.selectedPhaseValue = +params.phaseId;
        this.selectedMilestoneValue = +params.milestoneId;
        this.selectedTaskPackageValue = +params.packageId;

        if (this.projectConfiguration) {
          this.selectedPhase = this.projectConfiguration.find(p => p.id == this.selectedPhaseValue);
          this.selectedMilestone = this.selectedPhase?.milestones.find(m => m.id == this.selectedMilestoneValue);
          this.selectedTaskPackage = this.selectedMilestone?.taskPackages.find(
            tp => tp.id == this.selectedTaskPackageValue
          );

          if (this.selectedPhase == null) {
            this.selectedPhase = this.projectConfiguration.at(0);
            this.selectedPhaseValue = this.selectedPhase.id;
          }
          if (this.selectedMilestone == null) {
            this.selectedMilestone = this.selectedPhase?.milestones.at(0);
            this.selectedMilestoneValue = this.selectedMilestone.id;
          }
          if (this.selectedTaskPackage == null) {
            this.selectedTaskPackage = this.selectedMilestone?.taskPackages.at(0);
            this.selectedTaskPackageValue = this.selectedTaskPackage.id;
          }
        }

        this.loadTasks();
      }
    });
  }

  public ngOnInit() {
    this.templateService.loadAreaTypes();
    this.templateService.loadProjectFolders();
    this.templateService.loadGridFolders();
    this.dataAccessService.loadDepartments();
    this.configService.loadProjectConfiguration();
    this.getListOfProjects();
    this.getListOfSites();

    this.projectConfigurationSubscription = this.configService.projectConfiguration$.subscribe(projectConfiguration => {
      if (projectConfiguration) {
        this.projectConfiguration = projectConfiguration.phases;
        this.milestonesList = this.projectConfiguration?.flatMap(phase => phase.milestones);
        this.mapConfigurationTree();

        this.selectedPhase = this.projectConfiguration.find(p => p.id == this.selectedPhaseValue);
        this.selectedMilestone = this.selectedPhase?.milestones.find(m => m.id == this.selectedMilestoneValue);
        this.selectedTaskPackage = this.selectedMilestone?.taskPackages.find(
          tp => tp.id == this.selectedTaskPackageValue
        );
      }
      this.cdr.markForCheck();
    });

    this.areaTypesSubscription = this.templateService.areaTypes$.subscribe(areaTypes => {
      this.areaTypeList = areaTypes;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy() {
    this.areaTypesSubscription.unsubscribe();
    this.projectConfigurationSubscription.unsubscribe();
    this.routeParamsSubscription.unsubscribe();
  }

  get taskCount() {
    return (
      this.tasksTree.filter(t => t.type !== TaskTypeEnum.Group).length +
      this.tasksTree.flatMap(t => t.children).filter(t => t.type !== TaskTypeEnum.Group).length
    );
  }

  get groupCount() {
    return this.tasksTree.filter(t => t.type === TaskTypeEnum.Group).length;
  }

  public async loadTasks() {
    if (!this.selectedTaskPackageValue) {
      this.allTasks = [];
      this.tasksTree = [];
      this.TaskPackageNr = null;
      this.cdr.markForCheck();
      return;
    }
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const result = await this.templateService.getProjectTaskConfigList({
        taskPackageId: this.selectedTaskPackageValue,
      });
      if (!result.hasError) {
        this.allTasks = result.tasks;
        this.tasksTree = this.mapConfigTasksToTree(
          result.tasks.filter(task => task.taskPackageId == this.selectedTaskPackageValue)
        );
        this.TaskPackageNr = result.nr;
        this.mapConfigurationTree();
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private mapConfigTasksToTree(tasks: ConfigTask[]): MappedConfigTask[] {
    const taskMap = new Map<number, MappedConfigTask>();
    const rootTasks: MappedConfigTask[] = [];

    // First pass: create all task objects
    tasks.forEach(task => {
      const mappedTask: MappedConfigTask = {
        ...task,
        children: [],
        validation: task.validation ?? false,
      };
      if (task.id) {
        taskMap.set(task.id, mappedTask);
      }
    });

    // Second pass: build hierarchy
    tasks.forEach(task => {
      if (task.id) {
        const mappedTask = taskMap.get(task.id);
        if (mappedTask) {
          if (task.parentTaskId) {
            const parent = taskMap.get(task.parentTaskId);
            if (parent) {
              parent.children = parent.children || [];
              parent.children.push(mappedTask);
            }
          } else {
            rootTasks.push(mappedTask);
          }
        }
      }
    });

    return rootTasks;
  }

  private mapConfigurationTree() {
    const mappedConfigTree: MappedConfigTreeNode[] = [];
    this.projectConfiguration?.forEach(phase => {
      const mappedPhase: MappedConfigTreeNode = {
        nodeId: `phase-${phase.id}`,
        parentNodeId: null,
        title: `${phase.nr} | ${phase.title}`,
        conditionDisplay: null,
      };
      mappedConfigTree.push(mappedPhase);
      phase.milestones?.forEach(milestone => {
        const mappedMilestone: MappedConfigTreeNode = {
          nodeId: `milestone-${milestone.id}`,
          parentNodeId: `phase-${phase.id}`,
          title: `${milestone.nr} | ${milestone.name}`,
          conditionDisplay: null,
        };
        mappedConfigTree.push(mappedMilestone);
        milestone.taskPackages?.forEach(taskPackage => {
          const mappedTaskPackage: MappedConfigTreeNode = {
            nodeId: `taskPackage-${taskPackage.id}`,
            parentNodeId: `milestone-${milestone.id}`,
            title: `${taskPackage.nr} | ${taskPackage.name}`,
            conditionDisplay: null,
          };
          mappedConfigTree.push(mappedTaskPackage);
          const packageTasks = this.allTasks.filter(task => task.taskPackageId == taskPackage.id);
          packageTasks.forEach(task => {
            const mappedTask: MappedConfigTreeNode = {
              nodeId: `task-${task.id}`,
              parentNodeId: task.parentTaskId ? `task-${task.parentTaskId}` : `taskPackage-${taskPackage.id}`,
              id: task.id,
              title: `${task.nr} | ${task.title}`,
              conditionDisplay: task.conditionDisplay,
              type: task.type,
            };
            mappedConfigTree.push(mappedTask);
          });
        });
      });
    });
    this.configurationTree = mappedConfigTree;
    this.cdr.markForCheck();
  }

  public async writeTask(task: MappedConfigTask): Promise<boolean> {
    // If this is a new task, show confirmation

    try {
      // Rules (#4962) are only sent when the rules popup changed them; null leaves them untouched.
      const { rulesChanged, ...taskData } = task;
      const taskToWrite = {
        Task: { ...taskData, rules: rulesChanged ? (task.rules ?? []) : null },
        taskPackageId: this.selectedTaskPackageValue,
        SelectedProjectIdsWhereTasksNotRequired: task.SelectedProjectIdsWhereTasksNotRequired ?? this.selectedProjects,
        SelectedSiteIdsWhereTasksNotRequired: task.SelectedSiteIdsWhereTasksNotRequired ?? this.selectedSites,
      };

      const result = await this.templateService.writeProjectTaskConfigTask(taskToWrite, task.template);
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return false;
      }
      task.id = result.taskId;
      this.loadTasks();
      this.banner?.refresh();
      return true;
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
      return false;
    }
  }

  public async reorderTask(task: MappedConfigTask, previousTaskId: number, parentTaskId: number) {
    try {
      let payload = {
        TaskId: task.id,
        DropInTaskId: parentTaskId,
        PriorOrderingTaskId: previousTaskId,
      };

      const result = await this.templateService.reorderTaskConfigList(payload);
      if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
      else this.loadTasks();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  public async saveTask(saveEvent: { task: MappedConfigTask; callback: (success: boolean) => void }) {
    this.showConfirmationPopup = false;
    this.showSiteConfirmationPopup = false;
    const { task: updatedTask, callback } = saveEvent;

    if (updatedTask) {
      // Call the API to save the task first
      const success = await this.writeTask(updatedTask);

      if (success) {
        // Clear editing state
        this.editingData = null;

        // Trigger change detection
        this.cdr.markForCheck();

        // Notify the node component of success
        callback(true);
      } else {
        // API call failed, keep editing state and show error
        console.error('Failed to save task, keeping editing state');
        callback(false);
      }
    }
  }

  cancelTaskEdit(e: any) {
    this.editingData = null;
    if (e.id == null) {
      this.deleteTask(e);
    }
  }

  SavingTask(saveEvent: { task: MappedConfigTask; callback: (success: boolean) => void }) {
    const { task: updatedTask, callback } = saveEvent;

    this.savedEvent = { task: updatedTask, callback };

    if (updatedTask.id === null) {
      if (updatedTask.siteTask && this.siteList.length > 0) {
        this.showSiteConfirmationPopup = true;
        return;
      } else if (!updatedTask.siteTask && this.projectList.length > 0) {
        this.showConfirmationPopup = true;
        return;
      }
    }
    this.saveTask(saveEvent);
  }

  public deleteTask(taskToDelete: MappedConfigTask) {
    if (taskToDelete.id) {
      this.taskPendingDelete = taskToDelete;
      this.deletePopupVisible = true;
      this.cdr.markForCheck();
    } else {
      this.removeTaskFromTree(this.tasksTree, taskToDelete.id);
      this.cdr.markForCheck();
    }
  }

  confirmDelete(): void {
    if (!this.taskPendingDelete) return;
    const task = this.taskPendingDelete;
    this.deletePopupVisible = false;
    this.taskPendingDelete = null;
    this.removeTaskFromTree(this.tasksTree, task.id);
    this.cdr.markForCheck();
    this.writeTask({ ...task, isDeleted: true });
  }

  cancelDelete(): void {
    this.deletePopupVisible = false;
    this.taskPendingDelete = null;
  }

  private async reactivatePackageIfArchived(): Promise<void> {
    if (!this.selectedTaskPackage?.archived) return;
    await this.configService.unarchiveTaskPackageOnly(this.selectedTaskPackage.id);
    this.selectedTaskPackage.archived = null;
    this.cdr.markForCheck();
    notify($localize`:@@packageAutoReactivated:The task package was automatically reactivated.`, 'info', 4000);
  }

  public async addTask(parent?: MappedConfigTask) {
    await this.reactivatePackageIfArchived();
    let nr = this.selectedTaskPackage.nr;
    if (parent && parent.type === 'group') {
      nr = parent.nr;
    }
    nr += '.' + (parent ? parent.children.length + 1 : this.tasksTree.length + 1);
    const newTask: MappedConfigTask = {
      id: null,
      title: '',
      nr: nr,
      type: TaskTypeEnum.Task,
      areaTypeIds: [],
      siteTask: false,
      parentTaskId: parent?.id || undefined,
      children: [],
      validation: false,
      editing: true,
      priority: 3,
      estimatedHours: 0,
    };
    this.editingData = newTask;

    if (parent) {
      parent.children = parent.children || [];
      parent.children.push(newTask);
      // Ensure parent group is expanded to show the new task
      parent.expanded = true;
    } else {
      this.tasksTree.push(newTask);
    }
    this.cdr.markForCheck();

    // Scroll to the newly created task after DOM update
    setTimeout(() => {
      this.scrollToNewTask(newTask, parent);
    }, 100);
  }

  public async addGroup() {
    await this.reactivatePackageIfArchived();
    const newGroup: MappedConfigTask = {
      id: null,
      title: '',
      nr: this.selectedTaskPackage.nr + '.' + (this.tasksTree.length + 1),
      type: TaskTypeEnum.Group,
      areaTypeIds: [],
      siteTask: false,
      children: [],
      validation: false,
      editing: true,
      expanded: true,
    };

    this.tasksTree.push(newGroup);
    this.editingData = newGroup;

    // Trigger change detection
    this.cdr.markForCheck();

    // Scroll to the newly created group after DOM update
    setTimeout(() => {
      this.scrollToNewTask(newGroup);
    }, 100);
  }

  public onReorder(e: ReorderEvent) {
    const { fromIndex, toIndex, fromData, toData, itemData, dropInsideItem, fromComponent, toComponent } = e;

    let toItem = toData[toIndex];

    fromData.splice(fromIndex, 1);
    let fromParentId = parseInt(fromComponent.element().getAttribute('parentId'));
    let toParentId = parseInt(toComponent.element().getAttribute('parentId'));
    fromParentId = fromParentId ? fromParentId : null;
    toParentId = toParentId ? toParentId : null;

    if (dropInsideItem) {
      toItem.children.push(itemData);
      const prioId = toItem.children.length == 1 ? null : toItem.children[toItem.children.length - 2].id;
      this.reorderTask(itemData, prioId, toItem.id);
    } else if (toParentId == null && fromParentId != null) {
      toData.splice(toIndex, 0, itemData);
      const prioId = toIndex == 0 ? null : toData[toIndex - 1]?.id;
      this.reorderTask(itemData, prioId, toParentId);
    } else if (fromParentId == null && toParentId != null) {
      toItem = fromData.find(x => x.id == toParentId);
      toItem.children.splice(toIndex, 0, itemData);
      const prioId = toIndex == 0 ? null : toItem.children[toIndex - 1]?.id;
      this.reorderTask(itemData, prioId, toItem.id);
    } else {
      toData.splice(toIndex, 0, itemData);
      const prioId = toIndex == 0 ? null : toData[toIndex - 1]?.id;
      this.reorderTask(itemData, prioId, toParentId);
    }

    // Trigger change detection
    this.cdr.markForCheck();
  }

  public startReorder(e: DragStartEvent) {
    if (this.editingData != null) {
      e.cancel = true;
    }
    e.itemData = e.fromData[e.fromIndex];

    e.itemData.expanded = false;
  }

  public onDragMove(e: DragChangeEvent) {
    const { fromIndex, toIndex, fromData, toData, itemData, dropInsideItem } = e;

    const toItem = toData[toIndex];

    if (dropInsideItem && toItem.type !== TaskTypeEnum.Group) {
      e.cancel = true;
    }
    if (itemData.type === TaskTypeEnum.Group && toItem.type === TaskTypeEnum.Group && dropInsideItem) {
      e.cancel = true;
    }

    // Only archived tasks may be placed inside an already-archived group.
    // Block an active (non-archived) item from being dropped into an archived group.
    const targetGroup = this.getDropTargetGroup(e);
    if (targetGroup?.archived && !itemData.archived) {
      e.cancel = true;
    }
  }

  // Resolves the group an item is being dropped into, for both drop paths:
  // dropping onto a group header, and dropping among a group's expanded children.
  private getDropTargetGroup(e: DragChangeEvent): MappedConfigTask | null {
    const { toIndex, toData, dropInsideItem, toComponent } = e;
    const toItem = toData[toIndex];

    if (dropInsideItem && toItem?.type === TaskTypeEnum.Group) {
      return toItem;
    }

    const parentIdAttr = toComponent?.element().getAttribute('parentId');
    const toParentId = parentIdAttr ? parseInt(parentIdAttr) : null;
    if (toParentId) {
      return this.tasksTree.find(t => t.id === toParentId) ?? null;
    }

    return null;
  }

  private removeTaskFromTree(tasks: MappedConfigTask[], taskId: number): boolean {
    for (let i = 0; i < tasks.length; i++) {
      if (tasks[i].id === taskId) {
        tasks.splice(i, 1);
        return true;
      }
      if (tasks[i].children && this.removeTaskFromTree(tasks[i].children!, taskId)) {
        return true;
      }
    }
    return false;
  }

  // Helper method to flatten tree for API calls when needed
  public flattenTreeToConfigTasks(): ConfigTask[] {
    const flatTasks: ConfigTask[] = [];

    const flatten = (tasks: MappedConfigTask[], parentId?: number) => {
      tasks.forEach(task => {
        const { editing, expanded, children, ...configTask } = task;
        flatTasks.push({ ...configTask, parentTaskId: parentId });

        if (children && children.length > 0) {
          flatten(children, task.id);
        }
      });
    };

    flatten(this.tasksTree);
    return flatTasks;
  }

  // Helper method to scroll to a newly created task
  private scrollToNewTask(newTask: MappedConfigTask, parent?: MappedConfigTask) {
    if (!this.scrollView || !this.scrollView.instance) {
      return;
    }

    try {
      // Find the task element in the DOM
      let taskElement: HTMLElement | null = null;

      if (parent) {
        // For child tasks, find the task within the parent's children
        const parentElements = document.querySelectorAll('.task-tree-item');
        for (const parentEl of Array.from(parentElements)) {
          const configTaskNodes = parentEl.querySelectorAll('app-config-task-node');
          for (const taskNode of Array.from(configTaskNodes)) {
            const taskComponent = (taskNode as any)?.__ngContext__?.[8];
            if (taskComponent?.task === newTask) {
              taskElement = taskNode as HTMLElement;
              break;
            }
          }
          if (taskElement) break;
        }
      } else {
        // For root level tasks, find the last task element
        const taskElements = document.querySelectorAll('.task-tree-item > app-config-task-node');
        const lastElement = taskElements[taskElements.length - 1] as HTMLElement;
        if (lastElement) {
          taskElement = lastElement;
        }
      }

      if (taskElement) {
        // Get the scroll view instance and scroll to the element
        const scrollViewInstance = this.scrollView.instance;
        const scrollViewElement = scrollViewInstance.content();
        const taskElementRect = taskElement.getBoundingClientRect();
        const scrollViewRect = scrollViewElement.getBoundingClientRect();

        // Calculate the scroll position
        const scrollTop = scrollViewInstance.scrollTop();
        const targetScrollTop = scrollTop + taskElementRect.top - scrollViewRect.top - 100; // 100px offset from top

        scrollViewInstance.scrollTo({ top: Math.max(0, targetScrollTop) });
      }
    } catch (error) {
      console.warn('Could not scroll to new task:', error);
    }
  }

  // Project selection methods (adapted from templates-taskconfig)
  public getListOfProjects(): void {
    let payload = {};

    this.templateService.GetProjectForFilter(payload).then((result: any) => {
      this.completedProjectCount = result.projects.filter(
        (project: any) => project.status === ProjectStatus.COMPLETED || project.status === ProjectStatus.CANCELED
      ).length;

      this.projectList = result.projects
        .filter((project: any) => project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELED)
        .map((project: any) => ({
          id: project.id,
          name: project.name,
          projectNr: project.projectNr,
          areaType: project.areaTypeId,
          status: project.status,
        }));
    });
  }

  public getProjectMilestones(): void {
    this.milestonesList = this.projectConfiguration?.flatMap(phase => phase.milestones);
  }

  public isProjectSelected(projectId: any): boolean {
    return this.selectedProjects.includes(projectId);
  }

  public updateSelectedProjects() {
    if (this.selectedPhases.length === 0 && this.selectedAreas.length === 0 && this.selectedMilestones.length === 0) {
      this.filteredProjects = [];

      if (this.isAllSelected) {
        this.selectedProjects = this.projectList
          .map(project => project.id)
          .filter(id => !this.deselectedProjects.includes(id));
      } else {
        this.selectedProjects = this.manualProjects;
      }
    } else {
      const matchingByPhase =
        this.selectedPhases.length > 0
          ? this.projectList.filter(project => this.selectedPhases.includes(project.phase))
          : [];

      const matchingByArea =
        this.selectedAreas.length > 0
          ? this.projectList.filter(project => this.selectedAreas.includes(project.areaType))
          : [];

      const matchingByMilestone =
        this.selectedMilestones.length > 0
          ? this.projectList.filter(project => {
              if (Array.isArray(project.milestones)) {
                return project.milestones.some(m => this.selectedMilestones.includes(m));
              } else {
                return this.selectedMilestones.includes(project.milestones);
              }
            })
          : [];

      const unionProjects = [...matchingByPhase, ...matchingByArea, ...matchingByMilestone].map(p => p.id);

      this.filteredProjects = Array.from(new Set(unionProjects));

      const allSelectedProjects = Array.from(
        new Set([
          ...this.manualProjects,
          ...this.filteredProjects.filter(project => !this.deselectedProjects.includes(project)),
        ])
      );
      this.selectedProjects = allSelectedProjects;
    }

    this.projectList.forEach(project => {
      project.selected = this.selectedProjects.includes(project.id);
    });
  }

  public onRowClick(event: any) {
    const clickedId = event.data.id;
    const project = this.projectList.find(p => p.id === clickedId);

    project.selected = !project.selected;

    const isInManual = this.manualProjects.includes(clickedId);
    const isInSelected = this.selectedProjects.includes(clickedId);
    const isInDeselected = this.deselectedProjects.includes(clickedId);

    if (isInManual) {
      this.manualProjects = this.manualProjects.filter(id => id !== clickedId);
    } else if (isInSelected) {
      if (isInDeselected) {
        this.deselectedProjects = this.deselectedProjects.filter(id => id !== clickedId);
      } else {
        this.deselectedProjects.push(clickedId);
      }
    } else {
      this.manualProjects.push(clickedId);
    }

    if (this.manualProjects.length > 0 && this.grid?.instance) {
      this.grid.instance.selectRows(this.manualProjects, true);
    }

    this.updateSelectedProjects();
  }

  // ponytail: Select All is all-or-nothing, so the header check box is hidden while a filter hides
  // rows - no "all, or all visible?" question to answer and no scope state to keep.
  // Driven by onContentReady: the grid re-renders its rows after search text and after a header
  // filter alike, so one event covers both, and it lands exactly when rows start being hidden.
  public onGridContentReady() {
    const grid = this.grid?.instance;
    const filterActive = !!grid && (!!grid.option('searchPanel.text') || !!grid.getCombinedFilter());
    if (filterActive === this.isFilterActive) {
      return;
    }
    this.isFilterActive = filterActive;
    this.cdr.markForCheck();
  }

  public selectAll(e: any) {
    if (this.isAllSelected) {
      const allProjectIds = this.projectList.map(project => project.id);

      this.deselectedProjects = [];
      this.manualProjects = [];

      this.projectList.forEach(project => {
        project.selected = true;
      });

      this.selectedProjects = allProjectIds;

      if (this.grid?.instance) {
        this.grid.instance.selectRows(allProjectIds, false);
      }
    } else {
      this.selectedPhases = [];
      this.selectedAreas = [];
      this.selectedMilestones = [];
      this.deselectedProjects = [];
      this.manualProjects = [];

      this.projectList.forEach(project => {
        project.selected = false;
      });

      this.selectedProjects = [];

      if (this.grid?.instance) {
        this.grid.instance.deselectAll();
      }
    }
  }

  public saveUpdates() {
    if (this.savedEvent) {
      this.savedEvent.task.SelectedProjectIdsWhereTasksNotRequired = [...this.selectedProjects];
      this.saveTask(this.savedEvent);
    }
    this.selectedProjects = [];
    this.showConfirmationPopup = false;
    this.showProjects = false;
  }

  public showProjectList() {
    this.isAllSelected = false;
    this.deselectedProjects = [];
    this.manualProjects = [];
    this.selectedProjects = [];
    this.filteredProjects = [];
    this.projectList.forEach(p => { p.selected = false; });
    // ponytail: clearFilter() with no argument drops search text and header filters together,
    // so every open starts from the full list
    this.grid?.instance?.clearFilter();
    this.showProjects = true;
  }

  public saveConfirmedTask() {
    this.saveTask(this.savedEvent);
  }

  // Site selection methods
  public getListOfSites(): void {
    this.templateService.GetGridConnectionForFilter({}).then((result: any) => {
      if (!result.hasError) {
        this.siteList = (result.sites ?? [])
          .filter((site: any) => site.hasActiveProjects)
          .map((site: any) => ({
            id: site.id,
            name: site.name,
            nr: site.nr,
          }));
      }
    });
  }

  public isSiteSelected(siteId: any): boolean {
    return this.selectedSites.includes(siteId);
  }

  public onSiteRowClick(event: any) {
    const clickedId = event.data.id;
    const site = this.siteList.find(s => s.id === clickedId);
    site.selected = !site.selected;

    const isInManual = this.manualSites.includes(clickedId);
    const isInSelected = this.selectedSites.includes(clickedId);
    const isInDeselected = this.deselectedSites.includes(clickedId);

    if (isInManual) {
      this.manualSites = this.manualSites.filter(id => id !== clickedId);
    } else if (isInSelected) {
      if (isInDeselected) {
        this.deselectedSites = this.deselectedSites.filter(id => id !== clickedId);
      } else {
        this.deselectedSites.push(clickedId);
      }
    } else {
      this.manualSites.push(clickedId);
    }

    this.updateSelectedSites();
  }

  public updateSelectedSites() {
    if (this.isAllSitesSelected) {
      this.selectedSites = this.siteList
        .map(s => s.id)
        .filter(id => !this.deselectedSites.includes(id));
    } else {
      this.selectedSites = this.manualSites;
    }
    this.siteList.forEach(site => {
      site.selected = this.selectedSites.includes(site.id);
    });
  }

  public selectAllSites(e: any) {
    if (this.isAllSitesSelected) {
      const allSiteIds = this.siteList.map(s => s.id);
      this.deselectedSites = [];
      this.manualSites = [];
      this.siteList.forEach(s => { s.selected = true; });
      this.selectedSites = allSiteIds;
      if (this.siteGrid?.instance) {
        this.siteGrid.instance.selectRows(allSiteIds, false);
      }
    } else {
      this.deselectedSites = [];
      this.manualSites = [];
      this.siteList.forEach(s => { s.selected = false; });
      this.selectedSites = [];
      if (this.siteGrid?.instance) {
        this.siteGrid.instance.deselectAll();
      }
    }
  }

  public saveSiteUpdates() {
    if (this.savedEvent) {
      this.savedEvent.task.SelectedSiteIdsWhereTasksNotRequired = [...this.selectedSites];
      this.saveTask(this.savedEvent);
    }
    this.selectedSites = [];
    this.showSiteConfirmationPopup = false;
    this.showSites = false;
  }

  public showSiteList() {
    this.isAllSitesSelected = false;
    this.deselectedSites = [];
    this.manualSites = [];
    this.selectedSites = [];
    this.siteList.forEach(s => { s.selected = false; });
    this.showSites = true;
  }

  cancelArchive(): void {
    this.archivePopupVisible = false;
    this.taskPendingArchive = null;
    this.archiveSetNotRequired = false;
  }

  cancelReactivate(): void {
    this.reactivatePopupVisible = false;
    this.taskPendingReactivate = null;
  }

  async onArchive(task: MappedConfigTask): Promise<void> {
    const impact = await this.templateService.getArchiveImpact(task.id);
    this.archiveImpactTasks = impact?.affectedTasks ?? [];
    this.archiveOpenTaskCount = impact?.openProjectTaskCount ?? 0;
    this.archiveSetNotRequired = false;
    this.taskPendingArchive = task;
    this.archivePopupVisible = true;
    this.cdr.markForCheck();
  }

  async confirmArchive(): Promise<void> {
    // Setting tasks NOT REQUIRED rewrites rows across every project, so the call can run for a while.
    // Guard against a second submit: archiving twice re-stamps Archived and repeats the schedule rewrite.
    if (!this.taskPendingArchive?.id || this.archiveInProgress) return;

    this.archiveInProgress = true;
    this.cdr.markForCheck();
    try {
      const result = await this.templateService.archiveTaskConfig(
        this.taskPendingArchive.id,
        this.archiveSetNotRequired
      );

      if (result?.hasError === true) {
        // Keep the popup open so the user can retry rather than silently reporting success.
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }

      this.archivePopupVisible = false;
      this.taskPendingArchive = null;
      this.archiveSetNotRequired = false;
      this.loadTasks();
    } finally {
      this.archiveInProgress = false;
      this.cdr.markForCheck();
    }
  }

  onReactivate(task: MappedConfigTask): void {
    this.taskPendingReactivate = task;
    this.reactivatePopupVisible = true;
    this.cdr.markForCheck();
  }

  async confirmReactivate(): Promise<void> {
    if (!this.taskPendingReactivate?.id) return;
    await this.templateService.unarchiveTaskConfig(this.taskPendingReactivate.id);
    this.reactivatePopupVisible = false;
    this.taskPendingReactivate = null;
    await this.reactivatePackageIfArchived();
    this.loadTasks();
  }

  showOverview() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  selectedPhase = null;
  selectedPhaseValue = null;
  selectedMilestone = null;
  selectedMilestoneValue = null;
  selectedTaskPackage = null;
  selectedTaskPackageValue = null;
  selectPhase(e: ValueChangedEvent) {
    if (this.selectedPhase != e.value) {
      const phase = this.projectConfiguration.find(p => p.id == e.value);
      const milestone = phase?.milestones.at(0);
      const taskPackage = milestone?.taskPackages.at(0);
      this.selectedPhaseValue = phase?.id;
      this.selectedMilestoneValue = milestone?.id;
      this.selectedTaskPackageValue = taskPackage?.id;
      this.router.navigate(['tasks'], {
        relativeTo: this.route.parent,
        queryParams: {
          packageId: this.selectedTaskPackageValue,
          milestoneId: this.selectedMilestoneValue,
          phaseId: this.selectedPhaseValue,
        },
      });
    }
  }

  selectMilestone(e: ValueChangedEvent) {
    if (this.selectedMilestone != e.value) {
      const milestone = this.milestonesList.find(m => m.id == e.value);
      const taskPackage = milestone?.taskPackages.at(0);
      this.selectedMilestoneValue = milestone?.id;
      this.selectedTaskPackageValue = taskPackage?.id;
      this.router.navigate(['tasks'], {
        relativeTo: this.route.parent,
        queryParams: {
          packageId: this.selectedTaskPackageValue,
          milestoneId: this.selectedMilestoneValue,
          phaseId: this.selectedPhaseValue,
        },
      });
    }
  }

  selectTaskPackage(e: ValueChangedEvent) {
    if (this.selectedTaskPackage != e.value) {
      const taskPackage = this.milestonesList.flatMap(m => m.taskPackages).find(tp => tp.id === e.value);
      this.selectedTaskPackageValue = taskPackage?.id;
      this.router.navigate(['tasks'], {
        relativeTo: this.route.parent,
        queryParams: {
          packageId: this.selectedTaskPackageValue,
          milestoneId: this.selectedMilestoneValue,
          phaseId: this.selectedPhaseValue,
        },
      });
    }
  }

  onStartEdit(task: MappedConfigTask) {
    this.editingData = task;
    this.selectedTasks = [];
    this.cdr.markForCheck();
  }

  onTaskClick(task: MappedConfigTask) {
    if(this.editingData){
      return
    }
    if(this.selectedTasks.includes(task.id)){
      this.selectedTasks = this.selectedTasks.filter(x => x != task.id);
      if(task.type == TaskTypeEnum.Group){
        this.selectedTasks = this.selectedTasks.filter(x => !task.children.map(x => x.id).includes(x));
      }
    } else {
      this.selectedTasks.push(task.id);
      if(task.type == TaskTypeEnum.Group){
          this.selectedTasks = this.selectedTasks.concat(task.children.map(x => x.id));
      }
      
    }
  }

  moveTasks(){
    this.showMoveTasks = {
      tasks : this.selectedTasks,
      phaseId: this.selectedPhaseValue,
      milestoneId: this.selectedMilestoneValue,
      taskPackageId: null,
    }
  }


  getFilteredMilestones(phaseId: number){
    return this.projectConfiguration.find(x => x.id == phaseId)?.milestones;
  }

  getFilteredTaskPackages(phaseId: number, milestoneId: number){
    return this.projectConfiguration.find(x => x.id == phaseId)?.milestones.find(x => x.id == milestoneId)?.taskPackages;
  }

  async onConfirmMoveTasks(){
    try {
      const result = await this.templateService.moveTasksToTaskPackage({
        taskIds: this.selectedTasks,
        taskPackageId: this.showMoveTasks.taskPackageId,
      });

      if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
      this.loadTasks();
      this.showMoveTasks = null;

    }catch(error){
      AppGlobals.ShowErrorMessage(error);
    }
  }
}
